import {dayKey} from './calendarRange';

// Employees' "Repos" / "Congé" and the shop's closed days, as served by the
// clicoo CalendarController (/api/calendar/rest-days, /api/calendar/hours) —
// the same `availabilities` rows the tablets create and enforce.
//
// A rest day is {id, employee_id, employee_name, days_of_week: [0..6] (0 = dimanche),
// start_time / end_time: decimal hours, start_date / end_date: 'YYYY-MM-DD', recurring}.
// A "Congé" is simply one covering every day of the week over its date range.

export const REST_DAYS_KEY = ['calendar-rest-days'];     // shared with Réglages > Calendrier
export const CALENDAR_HOURS_KEY = ['calendar-hours'];

export const REST_FULL_DAY = {start_time: 9, end_time: 23}; // tablets' "journée entière"
export const ALL_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

// days_of_week: 0 = dimanche … 6 = samedi, listed Monday first
export const WEEKDAY_CHIPS = [
    {id: 1, short: 'Lun'}, {id: 2, short: 'Mar'}, {id: 3, short: 'Mer'}, {id: 4, short: 'Jeu'},
    {id: 5, short: 'Ven'}, {id: 6, short: 'Sam'}, {id: 0, short: 'Dim'},
];

export const isLeave = (rest) => (rest.days_of_week || []).length === 7;
export const restKindLabel = (rest) => (isLeave(rest) ? 'Congé' : 'Repos');
export const isFullDayRest = (rest) =>
    rest.start_time <= REST_FULL_DAY.start_time && rest.end_time >= REST_FULL_DAY.end_time;

export const isRestActiveOnDay = (rest, date) => {
    if (!(rest.days_of_week || []).map(Number).includes(date.getDay())) return false;
    const key = dayKey(date);
    return (!rest.start_date || key >= rest.start_date) && (!rest.end_date || key <= rest.end_date);
};

// Rest days running on `date`, optionally for one employee
export const restDaysForDay = (restDays, date, employeeId = null) =>
    (restDays || []).filter(r =>
        (employeeId == null || String(r.employee_id) === String(employeeId)) && isRestActiveOnDay(r, date));

const decimalHour = (date) => date.getHours() + date.getMinutes() / 60;

// Rest day overlapping [start, end) for that employee, or null (same half-open
// check as the tablets' isSlotBlockedByRestDay)
export const findRestConflict = (restDays, employeeId, start, end) => {
    if (!employeeId || !(start instanceof Date) || isNaN(start)) return null;
    const from = decimalHour(start);
    const to = end instanceof Date && dayKey(end) === dayKey(start) ? decimalHour(end) : 24;
    return restDaysForDay(restDays, start, employeeId)
        .find(r => from < Number(r.end_time) && Number(r.start_time) < to) || null;
};

const DAY_KEYS_BY_INDEX = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Opening hours of that day ({closed, start, end}), or null when none are configured
export const getDayHours = (hours, date) => (hours ? hours[DAY_KEYS_BY_INDEX[date.getDay()]] || null : null);
export const isDayClosed = (hours, date) => Boolean(getDayHours(hours, date)?.closed);

export const formatDecimalHour = (value) => {
    const total = Math.round(Number(value) * 60);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

export const restHoursLabel = (rest) =>
    isFullDayRest(rest) ? 'Journée entière' : `${formatDecimalHour(rest.start_time)} – ${formatDecimalHour(rest.end_time)}`;

// ─── Several slots in one form ────────────────────────────────────────────────
// The form holds {employee_ids, start_date, end_date, recurring, slots: [{days_of_week,
// start_time, end_time}]} ("Lun, Mar 10h-19h" + "Mer 9h-20h"). The API and the tablets
// only know one days/hours pair per row, so each slot is saved as its own rest day.

// Stable React key of a slot, so removing one doesn't shuffle the others' inputs
let restSlotSeq = 0;
export const keyedSlot = (slot) => ({...slot, key: ++restSlotSeq});

const slotsOverlap = (a, b) =>
    Number(a.start_time) < Number(b.end_time) && Number(b.start_time) < Number(a.end_time);

// First problem found in the slots, or null. The same day may appear in several
// slots (9h-12h + 14h-18h) as long as their hours don't overlap.
export const restSlotsError = (slots) => {
    const label = (index, message) => (slots.length > 1
        ? `Créneau ${index + 1} : ${message.charAt(0).toLowerCase()}${message.slice(1)}`
        : message);

    for (let i = 0; i < slots.length; i++) {
        if (!slots[i].days_of_week.length) return label(i, 'Choisissez au moins un jour');
        if (Number(slots[i].end_time) <= Number(slots[i].start_time)) {
            return label(i, "L'heure de fin doit être après l'heure de début");
        }
    }
    for (let i = 0; i < slots.length; i++) {
        for (let j = i + 1; j < slots.length; j++) {
            const day = WEEKDAY_CHIPS.find(d =>
                slots[i].days_of_week.includes(d.id) && slots[j].days_of_week.includes(d.id));
            if (day && slotsOverlap(slots[i], slots[j])) {
                return `${day.short} : les créneaux ${i + 1} et ${j + 1} se chevauchent`;
            }
        }
    }
    return null;
};

// One API payload per slot
export const restSlotPayloads = ({slots, ...common}) =>
    slots.map(({days_of_week, start_time, end_time}) => ({...common, days_of_week, start_time, end_time}));

// Saves the slots in order: when editing, the first one updates the existing row
// and the others are new rows. Stops at the first failure and reports how many
// went through, so that only the remaining slots get retried (no duplicates).
export const saveRestSlots = async (form, {id = null, create, update}) => {
    const payloads = restSlotPayloads(form);
    let saved = 0;
    try {
        for (const payload of payloads) {
            if (id && saved === 0) {
                const {employee_ids, ...changes} = payload;
                await update(id, {...changes, employee_id: employee_ids[0]});
            } else {
                await create(payload);
            }
            saved += 1;
        }
        return {saved, total: payloads.length, error: null};
    } catch (error) {
        return {saved, total: payloads.length, error};
    }
};

// Diagonal hatch used for every rest / closed area of the grids
export const REST_HATCH = {
    backgroundImage: 'repeating-linear-gradient(135deg, rgba(107,114,128,0.14) 0 6px, transparent 6px 12px)',
};
